import { useNavigate, Link } from 'react-router-dom';
import { gradientText, theme } from '../theme';
import { getUserCrushes, Crush, incrementContactClicks } from '../services/searchService';
import { useUser } from '../hooks/useUser';
import { toast } from 'react-hot-toast';
import { Heart, User, Phone, Loader, Clock, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCachedData } from '../hooks/useCachedData';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useState } from 'react';

function MyCrush() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { t, i18n } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Utiliser useCachedData pour charger et mettre en cache les crushes
  const {
    data: crushes,
    isLoading: loading,
    invalidateCache,
    refresh
  } = useCachedData<Crush[]>(
    user ? `user-crushes-${user.id}` : 'no-user-crushes',
    async () => {
      if (!user) return [];
      try {
        return await getUserCrushes(user.id);
      } catch (error) {
        console.error('Erreur lors du chargement des crushes:', error);
        toast.error(t('myCrush.deleteError'));
        return [];
      }
    },
    // Durée du cache: 30 minutes
    30 * 60 * 1000,
    [user?.id]
  );

  // Nouvelle fonction pour gérer le retour à la page précédente
  const handleGoBack = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(-1); // Retourne à la page précédente dans l'historique
  };
  
  // Fonction pour actualiser la liste des crushes
  const handleRefresh = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    await invalidateCache();
    toast.success(t('myCrush.refreshSuccess') || 'Liste actualisée');
    setIsRefreshing(false);
  };

  // Fonction pour supprimer un crush
  const handleDeleteCrush = async (crushId: string, isAnnonce: boolean = false) => {
    if (!user) return;

    try {
      // Appel API pour supprimer le crush
      const { supabase } = await import('../lib/supabase');
      
      // Utiliser la table appropriée en fonction du type de profil
      const tableName = isAnnonce ? 'crush_annonces' : 'crushes';
      
      await supabase.from(tableName).delete().eq('id', crushId);
      
      // Invalidation du cache et rafraichissement des données
      await invalidateCache();
      toast.success(t('myCrush.deleteSuccess'));
      // Déclencher un événement personnalisé pour signaler la mise à jour des crushs
      window.dispatchEvent(new Event('crushes-updated'));
    } catch (error) {
      console.error('Erreur lors de la suppression du crush:', error);
      toast.error(t('myCrush.deleteError'));
      // Rafraîchir les données en cas d'erreur
      refresh();
    }
  };

  // Fonction pour gérer les clics sur le contact
  const handleContactClick = async (e: React.MouseEvent, crushId: string, url: string) => {
    e.preventDefault();
    
    // Ouvrir le lien dans un nouvel onglet immédiatement
    window.open(url, '_blank');
    
    // Incrémenter le compteur de clics de manière asynchrone après
    await incrementContactClicks(crushId);
  };

  // Fonction pour formater le temps restant avant expiration
  const formatTimeUntilExpiration = (expiresAt: string) => {
    const locale = i18n.language === 'fr' ? fr : enUS;
    return formatDistanceToNow(new Date(expiresAt), { 
      addSuffix: true,
      locale 
    });
  };

  return (
    <div className="min-h-screen bg-black p-4">
      <div className="flex justify-between items-center mb-6">
        <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity" onClick={handleGoBack}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="url(#backArrowGradient)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-6 h-6"
          >
            <defs>
              <linearGradient id="backArrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={theme.colors.primary.start} />
                <stop offset="100%" stopColor={theme.colors.primary.end} />
              </linearGradient>
            </defs>
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </Link>
        
        <button 
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-1 text-gray-400 hover:text-gray-300 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          <span className="text-xs">{t('myCrush.refresh')}</span>
        </button>
      </div>
      
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-6">
          <h2 className={`text-3xl lg:text-4xl xl:text-5xl font-bold ${gradientText} mb-1`}>
            {t('myCrush.title')}
          </h2>
          <p className="text-gray-400 text-xs lg:text-sm italic font-light tracking-wide">
            {t('myCrush.subtitle')}
          </p>
          <p className="text-gray-500 text-xs mt-1 italic">
            {t('myCrush.expirationInfo')}
          </p>
        </div>
        
        <div className="bg-[#0A0A0A] rounded-2xl p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader className="w-8 h-8 text-primary-end animate-spin mb-4" />
              <p className="text-gray-400">{t('myCrush.loading')}</p>
            </div>
          ) : !crushes || crushes.length === 0 ? (
            <div className="text-center py-8">
              <Heart className="w-16 h-16 mx-auto text-gray-700 mb-4" />
              <p className="text-white font-medium mb-2">{t('myCrush.empty.title')}</p>
              <p className="text-gray-400 text-sm">
                {t('myCrush.empty.message')}
              </p>
              <button 
                onClick={() => navigate('/single-search')}
                className="mt-6 px-6 py-2 bg-gradient-to-r from-primary-start to-primary-end text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                {t('myCrush.empty.action')}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-white mb-4">
                {crushes.length > 1 
                  ? t('myCrush.count.plural', { count: crushes.length }) 
                  : t('myCrush.count.single', { count: crushes.length })}
              </p>
              
              {crushes.map((crush) => crush.profile && (
                <div key={crush.id} className="bg-[#121212] rounded-xl p-4 flex gap-4 w-full max-w-full overflow-hidden">
                  {/* Photo de profil avec âge superposé */}
                  <div className="w-20 h-20 min-w-[5rem] rounded-xl overflow-hidden flex-shrink-0 relative">
                    {crush.profile.avatar_url ? (
                      <img 
                        src={crush.profile.avatar_url} 
                        alt={crush.profile.full_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#1A1A1A]">
                        <User className="w-8 h-8 text-gray-600" />
                      </div>
                    )}
                    <div className="absolute bottom-0 right-0 bg-black/60 text-white text-xs font-medium py-1 px-2 rounded-tl-md">
                      {crush.profile.age} {t('singleSearch.results.age')}
                    </div>
                    
                    {/* Bouton supprimer */}
                    <button 
                      className="absolute top-0 right-0 w-7 h-7 bg-black/60 flex items-center justify-center rounded-bl-md"
                      onClick={() => handleDeleteCrush(crush.id, crush.is_annonce)}
                    >
                      <Heart className="w-4 h-4 text-primary-end fill-primary-end hover:text-white transition-colors" />
                    </button>
                  </div>
                  
                  {/* Informations */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col w-full">
                      <div className="flex items-center w-full justify-between">
                        <h4 className="text-white font-medium text-lg truncate pr-2 flex-1">
                          {crush.profile.full_name}
                        </h4>
                        {crush.profile.custom_link_url && (
                          <a 
                            href={crush.profile.custom_link_url}
                            onClick={(e) => crush.profile && handleContactClick(e, crush.profile.id, crush.profile.custom_link_url as string)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-primary-start to-primary-end hover:opacity-90 transition-opacity"
                          >
                            <Phone className="w-3 h-3 text-white" />
                          </a>
                        )}
                      </div>
                      
                      <div className="text-gray-400 text-sm mt-1 truncate">
                        {crush.profile.country}{crush.profile.country && crush.profile.city && ', '}{crush.profile.city}
                      </div>
                      
                      <div className="text-gray-500 text-xs mt-2">
                        {crush.profile.relation_type === 'court' 
                          ? t('myCrush.relation.shortTerm')
                          : t('myCrush.relation.longTerm')}
                      </div>

                      {/* Expiration info */}
                      <div className="text-gray-500 text-xs mt-1 flex items-center">
                        <Clock className="w-3 h-3 mr-1 shrink-0" />
                        <span className="truncate">{t('myCrush.expiresIn')}: {formatTimeUntilExpiration(crush.expires_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyCrush; 